import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './EcommerceAdmin.css';
import { formatINR } from '../../utils/currency';

const EcommerceAdmin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    fetchSyncStatus();
    if (activeTab === 'products') fetchProducts();
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'reviews') fetchReviews();
    if (activeTab === 'coupons') fetchCoupons();
  }, [activeTab]);

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/api/ecommerce/admin/products/sync-status');
      if (response.data.success) {
        setSyncStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/admin/products');
      if (response.data.success) {
        setProducts(response.data.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/admin/orders');
      if (response.data.success) {
        setOrders(response.data.data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/admin/reviews');
      if (response.data.success) {
        setReviews(response.data.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/admin/coupons');
      if (response.data.success) {
        setCoupons(response.data.data.coupons);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/ecommerce/admin/products/sync');
      if (response.data.success) {
        alert('Products synced successfully!');
        fetchSyncStatus();
        if (activeTab === 'products') fetchProducts();
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      alert('Error syncing products');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      if (activeTab === 'products') await fetchProducts();
      if (activeTab === 'orders') await fetchOrders();
      if (activeTab === 'reviews') await fetchReviews();
      if (activeTab === 'coupons') await fetchCoupons();
      await fetchSyncStatus();
    } catch (e) {
      // no-op; individual loaders already log
    }
  };

  const formatPrice = (price) => formatINR(price);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="ecommerce-admin">
      <div className="admin-header">
        <h1>E-commerce Management</h1>
        <div className="sync-status">
          {syncStatus && (
            <div className="status-card">
              <h3>Sync Status</h3>
              <p>Inventory Items: {syncStatus.totalInventory}</p>
              <p>E-commerce Products: {syncStatus.totalProducts}</p>
              <p>Sync Percentage: {syncStatus.syncPercentage}%</p>
              <button 
                onClick={handleSyncProducts} 
                disabled={loading}
                className="sync-btn"
              >
                {loading ? 'Syncing...' : 'Sync Products'}
              </button>
              <button 
                onClick={handleRefresh}
                disabled={loading}
                className="sync-btn"
                style={{ marginLeft: 8 }}
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'products' ? 'active' : ''}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button
          className={activeTab === 'reviews' ? 'active' : ''}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews
        </button>
        <button
          className={activeTab === 'coupons' ? 'active' : ''}
          onClick={() => setActiveTab('coupons')}
        >
          Coupons
        </button>
      </div>

      <div className="admin-content">
        {loading && <div className="loading">Loading...</div>}

        {/* Products Tab */}
        {activeTab === 'products' && !loading && (
          <div className="products-section">
            <div className="section-header">
              <h2>E-commerce Products</h2>
              <button onClick={handleSyncProducts} className="sync-btn">
                Sync with Inventory
              </button>
            </div>
            
            <div className="products-grid">
              {products.map((product) => (
                <div key={product._id} className="product-card">
                  <div className="product-image">
                    {product.inventory.images && product.inventory.images.length > 0 ? (
                      <img
                        src={product.inventory.images[0]}
                        alt={product.inventory.itemName}
                        onError={(e) => {
                          e.target.src = '/placeholder-product.jpg';
                        }}
                      />
                    ) : (
                      <div className="placeholder">No Image</div>
                    )}
                  </div>
                  
                  <div className="product-info">
                    <h3>{product.inventory.itemName}</h3>
                    <p className="product-code">{product.inventory.itemCode}</p>
                    <p className="product-price">
                      {formatPrice(product.inventory.sellingPrice)}
                    </p>
                    <p className="product-stock">
                      Stock: {product.inventory.itemQuantity}
                    </p>
                    <div className="product-status">
                      <span className={`status ${product.isActive ? 'active' : 'inactive'}`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {product.isFeatured && <span className="featured">Featured</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && !loading && (
          <div className="orders-section">
            <h2>Recent Orders</h2>
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.orderNumber}</td>
                      <td>{order.user.name}</td>
                      <td>{formatPrice(order.totalAmount)}</td>
                      <td>
                        <span className={`status ${order.status}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && !loading && (
          <div className="reviews-section">
            <h2>Product Reviews</h2>
            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review._id} className="review-card">
                  <div className="review-header">
                    <h4>{review.user.name}</h4>
                    <div className="review-rating">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`star ${i < review.rating ? 'filled' : ''}`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <span className="review-date">{formatDate(review.createdAt)}</span>
                  </div>
                  
                  {review.title && <h5>{review.title}</h5>}
                  {review.comment && <p>{review.comment}</p>}
                  
                  <div className="review-actions">
                    <span className={`approval ${review.isApproved ? 'approved' : 'pending'}`}>
                      {review.isApproved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && !loading && (
          <div className="coupons-section">
            <div className="section-header">
              <h2>Coupons</h2>
              <button className="add-btn">Add Coupon</button>
            </div>
            
            <div className="coupons-list">
              {coupons.map((coupon) => (
                <div key={coupon._id} className="coupon-card">
                  <div className="coupon-header">
                    <h3>{coupon.name}</h3>
                    <span className={`status ${coupon.isActive ? 'active' : 'inactive'}`}>
                      {coupon.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="coupon-details">
                    <p><strong>Code:</strong> {coupon.code}</p>
                    <p><strong>Type:</strong> {coupon.type}</p>
                    <p><strong>Value:</strong> {coupon.value}</p>
                    <p><strong>Valid:</strong> {formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}</p>
                    <p><strong>Usage:</strong> {coupon.usedCount} / {coupon.usageLimit || '∞'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EcommerceAdmin;
